import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FileText, Download, DollarSign, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '../lib/currency';

const fm = (v) => formatMoney(v, 'USD');

export default function MyPayslips() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlacement, setIsPlacement] = useState(false);

  useEffect(() => { fetchPayslips(); }, []);

  const fetchPayslips = async () => {
    try {
      const response = await api.get('/employee/payrolls');
      setPayslips(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Agent en placement : fiche de paie non accessible
        setIsPlacement(true);
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (payslipId, period) => {
    try {
      const response = await api.get(`/payslips/${payslipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiche_paie_${period}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Fiche de paie telechargee');
    } catch (error) {
      toast.error('Erreur lors du telechargement');
    }
  };

  const monthNames = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  // Agent en placement : accès à la fiche de paie non autorisé
  if (isPlacement) {
    return (
      <div className="space-y-6" data-testid="my-payslips-page">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Mes Fiches de Paie</h1>
        </div>
        <Card>
          <CardContent className="p-12 flex flex-col items-center justify-center text-center gap-4">
            <div className="p-4 rounded-full bg-slate-100">
              <Lock className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-700">Accès non disponible</h2>
            <p className="text-slate-500 max-w-sm">
              Les fiches de paie ne sont pas accessibles pour les agents en placement.
              Veuillez contacter votre responsable RH pour plus d'informations.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-payslips-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Mes Fiches de Paie</h1>
        <p className="text-slate-600 mt-1">Consultez et telechargez vos fiches de paie</p>
      </div>

      {payslips.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Aucune fiche de paie disponible</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {payslips.map(ps => (
            <Card key={ps.id} className="border-slate-200 hover:border-green-300 transition-colors" data-testid={`payslip-${ps.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-green-100">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {monthNames[(ps.month || 1) - 1]} {ps.year}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span>Brut: <strong className="text-slate-700">{fm(ps.gross_salary)}</strong></span>
                        <span>Retenues: <strong className="text-red-600">{fm(ps.total_deductions)}</strong></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Net a payer</p>
                      <p className="text-xl font-bold text-green-700">{fm(ps.net_pay)}</p>
                    </div>
                    <Button variant="outline" onClick={() => handleDownloadPDF(ps.id, ps.period)} data-testid={`download-${ps.id}`}>
                      <Download className="w-4 h-4 mr-2" /> PDF
                    </Button>
                  </div>
                </div>

                {/* Detail breakdown */}
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Salaire base</span>
                    <p className="font-medium">{fm(ps.base_salary)}</p>
                  </div>
                  {(ps.transport_allowance || 0) > 0 && (
                    <div>
                      <span className="text-slate-500">Prime transport</span>
                      <p className="font-medium">{fm(ps.transport_allowance)}</p>
                    </div>
                  )}
                  {(ps.housing_allowance || 0) > 0 && (
                    <div>
                      <span className="text-slate-500">Prime logement</span>
                      <p className="font-medium">{fm(ps.housing_allowance)}</p>
                    </div>
                  )}
                  {(ps.overtime_pay || 0) > 0 && (
                    <div>
                      <span className="text-slate-500">Heures sup.</span>
                      <p className="font-medium">{fm(ps.overtime_pay)}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-500">Jours travailles</span>
                    <p className="font-medium">{ps.days_worked} jours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
